import type { AxiosInstance } from "axios";
import queryString from "query-string";

import { Roles } from "api/schemas/shared";
import { getRolePathPrefix } from "api/services/utils";

import { type AddressAutoCompleteArgs, AddressResponseSchema } from "./hooks/useAddressAutocomplete";
import type { CareEvent } from "./models/CareEvent";
import { type AvailableAilmentsArgs, AvailableAilmentsSchema } from "./schemas/AvailableAilments";
import type { PostCareEventSuggestedArgs, PutCareEventBillingArgs } from "./schemas/CareEventBilling";
import { CareEventBillingSchema } from "./schemas/CareEventBilling";
import type { PaginationArgs, QueryOptionsArgs } from "./schemas/common";
import type { EarningsUsTherapistArgs } from "./schemas/EarningsUsTherapist";
import { HCProfessionalResponseSchema } from "./schemas/HCProfessional";
import type { GetManagerPatientsResponse } from "./schemas/ManagerBooking";
import { GetManagerPatientsResponseSchema } from "./schemas/ManagerBooking";
import type { GetPatientsArgsSchema } from "./schemas/Patients";
import { PatientsSchema } from "./schemas/Patients";
import { type FeelingReportArgs, FeelingReportSchema } from "./schemas/patients/FeelingReport";
import { type HealthJournal, type HealthJournalArgs, HealthJournalSchema } from "./schemas/patients/HealthJournal";
import { type SwitchLateralLocationArgs, SwitchLateralLocationSchema } from "./schemas/patients/SwitchLateralLocation";
import { type SwitchTreatmentProgramArgs } from "./schemas/patients/SwitchTreatmentPrograms";
import type { PostArgs, PutPhysicianMedicalReferralArgs, PutPhysicianPlanOfCareArgs } from "./schemas/physician";
import {
  HandledPatientSchema,
  HandledPatientsResponseSchema,
  PhysicianMedicalReferralSchema,
  PhysicianPlanOfCareSchema,
  UnhandledPatientResponseSchema,
  UnhandledPatientsResponseSchema,
} from "./schemas/physician";
import { type GetPotentialPayersArgs, PotentialPayerSchema } from "./schemas/PotentialPayer";
import type { GetSmartRepliesArgs } from "./schemas/SmartReply";
import { CommentRepliesSchema, MessageRepliesSchema } from "./schemas/SmartReply";
import type { PostTreatmentActivityLogArgs, PutTreatmentActivityLogArgs } from "./schemas/TreatmentActivityLog";
import {
  CareEventSuggestedSchema,
  PostTreatmentActivityLogResponseSchema,
  TreatmentActivityLogCategoriesSchema,
  TreatmentActivityLogSchema,
  TreatmentActivityLogsPatientSchema,
} from "./schemas/TreatmentActivityLog";
import type {
  CreateUserCourseTemplatePayload,
  PutUserCoursePayload,
  UpdateUserCourseTemplatePayload,
} from "./schemas/UserCourse";
import {
  GetUserCourseResponseSchema,
  GetUserCourseTemplateResponseSchema,
  GetUserCourseTemplatesResponseSchema,
} from "./schemas/UserCourse";
import { type UpdateInsuranceArgs, UpdateInsuranceResponseSchema } from "./schemas/UsInsurance";
import { UsStatesResponseSchema } from "./schemas/UsState";
import { type AdminService, createAdminService } from "./services/admin";
import type { AppointmentService } from "./services/appointment";
import { createAppointmentService } from "./services/appointment";
import { type ClinicPartnerService, createClinicPartnerService } from "./services/clinic_partner";
import { type ContentService, createContentService } from "./services/content";
import type { MessagingService } from "./services/messaging";
import { createMessagingService } from "./services/messaging";
import { type PatientService, createPatientService } from "./services/patient";
import type { TherapistService } from "./services/therapist";
import { createTherapistService } from "./services/therapist";
import { type TreatmentService, createTreatmentService } from "./services/treatment";
import type { GetAuthenticatedUserResponse } from "./types/GetAuthenticatedUser";
import type { GetCurrentCourseArgs, GetCurrentCourseResponse } from "./types/GetCurrentCourse";
import type { GetHealthReportsArgs, GetHealthReportsResponse } from "./types/GetHealthReportsResponse";
import type { GetHealthReportsV2Args, GetHealthReportsV2Response } from "./types/GetHealthReportsV2Response";
import type { GetMedicalReferralsResponse } from "./types/GetMedicalReferrals";
import type { GetPainGraphArgs, GetPainGraphResponse } from "./types/GetPainGraph";
import type { GetPatientArgs, GetPatientResponse } from "./types/GetPatient";
import type { GetPatientCareEventsArgs, GetPatientCareEventsResponse } from "./types/GetPatientCareEvents";
import type {
  GetPatientCurrentSubscriptionArgs,
  GetPatientCurrentSubscriptionResponse,
} from "./types/GetPatientCurrentSubscription";
import type { GetPatientGoalsArgs, GetPatientGoalsResponse } from "./types/GetPatientGoals";
import type { GetPatientIcdCodesArgs, GetPatientIcdCodesResponse } from "./types/GetPatientIcdCodes";
import type { GetPatientNoteArgs, GetPatientNoteResponse } from "./types/GetPatientNote";
import type { GetPatientUnreadCommentsCountResponse } from "./types/GetPatientUnreadCommentsCount";
import type {
  GetPatientUsProviderGroupProfileArgs,
  GetPatientUsProviderGroupProfileResponse,
} from "./types/GetPatientUsProviderGroupProfile";
import type {
  GetPatientWidgetsPainLocationArgs,
  GetPatientWidgetsPainLocationResponse,
} from "./types/GetPatientWidgetsPainLocation";
import type { GetPhysicalFunctionGraphArgs, GetPhysicalFunctionGraphResponse } from "./types/GetPhysicalFunctionGraph";
import type { GetPlansOfCareArgs, GetPlansOfCareResponse } from "./types/GetPlansOfCare";
import type { GetPTCareEventsArgs, GetPTCareEventsResponse } from "./types/GetPTCareEvents";
import type { GetTherapistEarningsArgs, GetTherapistEarningsResponse } from "./types/GetTherapistEarnings";
import type { GetUsPatientInsuranceArgs, GetUsPatientInsuranceResponse } from "./types/GetUsPatientInsurance";
import type {
  PostDismissAppointmentSuggestionArgs,
  PostDismissAppointmentSuggestionResponse,
} from "./types/PostDismissAppointmentSuggestion";
import type { PostMedicalNoteAddendumArgs } from "./types/PostMedicalNoteAddendum";
import type { PutPatientCareEventArgs, PutPatientCareEventResponse } from "./types/PutPatientCareEvent";
import type { PutPatientMarkAsPrioArgs, PutPatientMarkAsPrioResponse } from "./types/PutPatientMarkAsPrio";
import type { PutPlanOfCareArgs, PutPlanOfCareResponse } from "./types/PutPlanOfCare";
import type { PutTherapistArgs, PutTherapistResponse } from "./types/PutTherapist";
import { validateResponse } from "./validateResponse";

export class ApiClient {
  private transport: AxiosInstance;

  private transportV2: AxiosInstance;

  private transportLeifGPT: AxiosInstance;

  // Content service methods

  getExerciseImages: ContentService["getExerciseImages"];

  getExercises: ContentService["getExercises"];

  getExercise: ContentService["getExercise"];

  getLessons: ContentService["getLessons"];

  getLesson: ContentService["getLesson"];

  postExercise: ContentService["postExercise"];

  postExerciseLevel: ContentService["postExerciseLevel"];

  postExerciseLevelTranslation: ContentService["postExerciseLevelTranslation"];

  deleteExercise: ContentService["deleteExercise"];

  postFile: ContentService["postFile"];

  postLesson: ContentService["postLesson"];

  postLessonPart: ContentService["postLessonPart"];

  postLessonPartTranslation: ContentService["postLessonPartTranslation"];

  putExercise: ContentService["putExercise"];

  putExerciseLevel: ContentService["putExerciseLevel"];

  putExerciseLevelTranslation: ContentService["putExerciseLevelTranslation"];

  putLesson: ContentService["putLesson"];

  putLessonPart: ContentService["putLessonPart"];

  putLessonPartTranslation: ContentService["putLessonPartTranslation"];

  deleteLesson: ContentService["deleteLesson"];

  // Treatment service methods

  postPatientTreatmentStart: TreatmentService["postPatientTreatmentStart"];

  getPatientTreatmentSchedule: TreatmentService["getPatientTreatmentSchedule"];

  putPatientTreatmentSchedule: TreatmentService["putPatientTreatmentSchedule"];

  putPatientTreatmentScheduleActivity: TreatmentService["putPatientTreatmentScheduleActivity"];

  deletePatientTreatmentScheduleActivity: TreatmentService["deletePatientTreatmentScheduleActivity"];

  getPatientTreatmentScheduleWeek: TreatmentService["getPatientTreatmentScheduleWeek"];

  postConvertTreatmentTemplate: TreatmentService["postConvertTreatmentTemplate"];

  postTreatmentSchedule: TreatmentService["postTreatmentSchedule"];

  getTreatmentSchedules: TreatmentService["getTreatmentSchedules"];

  getTreatmentScheduleGroups: TreatmentService["getTreatmentScheduleGroups"];

  getTreatmentSchedule: TreatmentService["getTreatmentSchedule"];

  putTreatmentSchedule: TreatmentService["putTreatmentSchedule"];

  deleteTreatmentSchedule: TreatmentService["deleteTreatmentSchedule"];

  // Admin service methods

  getActivePatients: AdminService["getActivePatients"];

  getBillingPortalUrl: AdminService["getBillingPortalUrl"];

  getCommissionRates: AdminService["getCommissionRates"];

  getEarnings: AdminService["getEarnings"];

  getEnrolledPatients: AdminService["getEnrolledPatients"];

  getEnrollmentGoals: AdminService["getEnrollmentGoals"];

  getHealthCareProviders: AdminService["getHealthCareProviders"];

  getInvoices: AdminService["getInvoices"];

  getInvoiceSpecification: AdminService["getInvoiceSpecification"];

  getPendingTreatmentActivityLogs: AdminService["getPendingTreatmentActivityLogs"];

  getPerformanceSummary: AdminService["getPerformanceSummary"];

  getProfessionals: AdminService["getProfessionals"];

  getUnsignedNotes: AdminService["getUnsignedNotes"];

  postCommissionRates: AdminService["postCommissionRates"];

  putEnrollmentGoal: AdminService["putEnrollmentGoal"];

  // Messaging service methods

  deleteQuickReply: MessagingService["deleteQuickReply"];

  getBroadcastMessages: MessagingService["getBroadcastMessages"];

  getConversations: MessagingService["getConversations"];

  getConversationMessages: MessagingService["getConversationMessages"];

  getQuickReplies: MessagingService["getQuickReplies"];

  postBroadcastMessage: MessagingService["postBroadcastMessage"];

  postMessage: MessagingService["postMessage"];

  postQuickReply: MessagingService["postQuickReply"];

  putMarkMessageAsRead: MessagingService["putMarkMessageAsRead"];

  putPinMessage: MessagingService["putPinMessage"];

  putUnpinMessage: MessagingService["putUnpinMessage"];

  // Patient service methods

  getPatientProfile: PatientService["getPatientProfile"];

  getTreatmentAdherence: PatientService["getTreatmentAdherence"];

  // Appointment service methods

  getAppointmentsV1: AppointmentService["getAppointmentsV1"];

  postAppointmentV1: AppointmentService["postAppointmentV1"];

  putAppointmentCompleted: AppointmentService["putAppointmentCompleted"];

  deleteAppointmentV2: AppointmentService["deleteAppointmentV2"];

  getAppointmentsV2: AppointmentService["getAppointmentsV2"];

  getAppointmentsV2Patient: AppointmentService["getAppointmentsV2Patient"];

  postAppointmentV2: AppointmentService["postAppointmentV2"];

  postAppointmentV2NoShow: AppointmentService["postAppointmentV2NoShow"];

  putAppointmentV2: AppointmentService["putAppointmentV2"];

  deleteAppointmentV2Manager: AppointmentService["deleteAppointmentV2Manager"];

  getAppointmentsV2Manager: AppointmentService["getAppointmentsV2Manager"];

  postAppointmentV2Manager: AppointmentService["postAppointmentV2Manager"];

  putAppointmentV2Manager: AppointmentService["putAppointmentV2Manager"];

  deleteBookingInterval: AppointmentService["deleteBookingInterval"];

  postBookingInterval: AppointmentService["postBookingInterval"];

  putBookingInterval: AppointmentService["putBookingInterval"];

  postManagerBookingInterval: AppointmentService["postManagerBookingInterval"];

  deleteService: AppointmentService["deleteService"];

  getHcpServices: AppointmentService["getHcpServices"];

  getManagerServices: AppointmentService["getManagerServices"];

  getServiceTemplate: AppointmentService["getServiceTemplate"];

  postService: AppointmentService["postService"];

  putService: AppointmentService["putService"];

  // Therapist service methods

  getTherapistActivePatients: TherapistService["getTherapistActivePatients"];

  getTherapistCommissions: TherapistService["getTherapistCommissions"];

  getTherapistEnrolledPatients: TherapistService["getTherapistEnrolledPatients"];

  getTherapistEnrollments: TherapistService["getTherapistEnrollments"];

  getTherapistInvitations: TherapistService["getTherapistInvitations"];

  getTherapistPatientsFunctionality: TherapistService["getTherapistPatientsFunctionality"];

  getTherapistPatientsPain: TherapistService["getTherapistPatientsPain"];

  getTherapistRating: TherapistService["getTherapistRating"];

  postTherapistInvitation: TherapistService["postTherapistInvitation"];

  putResendTherapistInvitation: TherapistService["putResendTherapistInvitation"];

  // Clinic partner service methods
  getClinicPartnerActivePatients: ClinicPartnerService["getClinicPartnerActivePatients"];

  getClinicPartnerAvgFunctionality: ClinicPartnerService["getClinicPartnerAvgFunctionality"];

  getClinicPartnerAvgNps: ClinicPartnerService["getClinicPartnerAvgNps"];

  getClinicPartnerAvgPain: ClinicPartnerService["getClinicPartnerAvgPain"];

  getClinicPartnerEnrolledPatients: ClinicPartnerService["getClinicPartnerEnrolledPatients"];

  getClinicPartnerInvitations: ClinicPartnerService["getClinicPartnerInvitations"];

  getClinicPartnerPatients: ClinicPartnerService["getClinicPartnerPatients"];

  getClinicPartnersMembers: ClinicPartnerService["getClinicPartnersMembers"];

  getUsProviderGroupClinics: ClinicPartnerService["getUsProviderGroupClinics"];

  postClinicPartnerInvitation: ClinicPartnerService["postClinicPartnerInvitation"];

  postResendClinicPartnerInvitation: ClinicPartnerService["postResendClinicPartnerInvitation"];

  constructor(transport: AxiosInstance, transportV2: AxiosInstance, transportLeifGPT: AxiosInstance) {
    this.transport = transport;
    this.transportV2 = transportV2; // To use v2, this.transportV2 instead of this.transport
    this.transportLeifGPT = transportLeifGPT; // To use v2, this.transportV2 instead of this.transport

    const contentService = createContentService(this.transportV2);
    this.getExerciseImages = contentService.getExerciseImages;
    this.getExercises = contentService.getExercises;
    this.getExercise = contentService.getExercise;
    this.getLessons = contentService.getLessons;
    this.getLesson = contentService.getLesson;
    this.postExercise = contentService.postExercise;
    this.postExerciseLevel = contentService.postExerciseLevel;
    this.postExerciseLevelTranslation = contentService.postExerciseLevelTranslation;
    this.deleteExercise = contentService.deleteExercise;
    this.postFile = contentService.postFile;
    this.postLesson = contentService.postLesson;
    this.postLessonPart = contentService.postLessonPart;
    this.postLessonPartTranslation = contentService.postLessonPartTranslation;
    this.putExercise = contentService.putExercise;
    this.putExerciseLevel = contentService.putExerciseLevel;
    this.putExerciseLevelTranslation = contentService.putExerciseLevelTranslation;
    this.putLesson = contentService.putLesson;
    this.putLessonPart = contentService.putLessonPart;
    this.putLessonPartTranslation = contentService.putLessonPartTranslation;
    this.deleteLesson = contentService.deleteLesson;

    const treatmentService = createTreatmentService(this.transportV2);
    this.postPatientTreatmentStart = treatmentService.postPatientTreatmentStart;
    this.getPatientTreatmentSchedule = treatmentService.getPatientTreatmentSchedule;
    this.putPatientTreatmentSchedule = treatmentService.putPatientTreatmentSchedule;
    this.putPatientTreatmentScheduleActivity = treatmentService.putPatientTreatmentScheduleActivity;
    this.deletePatientTreatmentScheduleActivity = treatmentService.deletePatientTreatmentScheduleActivity;
    this.getPatientTreatmentScheduleWeek = treatmentService.getPatientTreatmentScheduleWeek;
    this.postConvertTreatmentTemplate = treatmentService.postConvertTreatmentTemplate;
    this.postTreatmentSchedule = treatmentService.postTreatmentSchedule;
    this.getTreatmentSchedules = treatmentService.getTreatmentSchedules;
    this.getTreatmentScheduleGroups = treatmentService.getTreatmentScheduleGroups;
    this.getTreatmentSchedule = treatmentService.getTreatmentSchedule;
    this.putTreatmentSchedule = treatmentService.putTreatmentSchedule;
    this.deleteTreatmentSchedule = treatmentService.deleteTreatmentSchedule;

    const adminService = createAdminService(this.transportV2);
    this.getActivePatients = adminService.getActivePatients;
    this.getBillingPortalUrl = adminService.getBillingPortalUrl;
    this.getCommissionRates = adminService.getCommissionRates;
    this.getEarnings = adminService.getEarnings;
    this.getEnrolledPatients = adminService.getEnrolledPatients;
    this.getEnrollmentGoals = adminService.getEnrollmentGoals;
    this.getHealthCareProviders = adminService.getHealthCareProviders;
    this.getInvoices = adminService.getInvoices;
    this.getInvoiceSpecification = adminService.getInvoiceSpecification;
    this.getPendingTreatmentActivityLogs = adminService.getPendingTreatmentActivityLogs;
    this.getPerformanceSummary = adminService.getPerformanceSummary;
    this.getProfessionals = adminService.getProfessionals;
    this.getUnsignedNotes = adminService.getUnsignedNotes;
    this.postCommissionRates = adminService.postCommissionRates;
    this.putEnrollmentGoal = adminService.putEnrollmentGoal;

    const messagingService = createMessagingService(this.transport);
    this.deleteQuickReply = messagingService.deleteQuickReply;
    this.getBroadcastMessages = messagingService.getBroadcastMessages;
    this.getConversations = messagingService.getConversations;
    this.getConversationMessages = messagingService.getConversationMessages;
    this.getQuickReplies = messagingService.getQuickReplies;
    this.postBroadcastMessage = messagingService.postBroadcastMessage;
    this.postMessage = messagingService.postMessage;
    this.postQuickReply = messagingService.postQuickReply;
    this.putMarkMessageAsRead = messagingService.putMarkMessageAsRead;
    this.putPinMessage = messagingService.putPinMessage;
    this.putUnpinMessage = messagingService.putUnpinMessage;

    const patientService = createPatientService({ transportV1: this.transport, transportV2: this.transportV2 });
    this.getPatientProfile = patientService.getPatientProfile;
    this.getTreatmentAdherence = patientService.getTreatmentAdherence;

    const appointmentService = createAppointmentService({ transportV1: this.transport, transportV2: this.transportV2 });
    this.getAppointmentsV1 = appointmentService.getAppointmentsV1;
    this.postAppointmentV1 = appointmentService.postAppointmentV1;
    this.putAppointmentCompleted = appointmentService.putAppointmentCompleted;
    this.deleteAppointmentV2 = appointmentService.deleteAppointmentV2;
    this.getAppointmentsV2 = appointmentService.getAppointmentsV2;
    this.getAppointmentsV2Patient = appointmentService.getAppointmentsV2Patient;
    this.postAppointmentV2 = appointmentService.postAppointmentV2;
    this.postAppointmentV2NoShow = appointmentService.postAppointmentV2NoShow;
    this.putAppointmentV2 = appointmentService.putAppointmentV2;
    this.deleteAppointmentV2Manager = appointmentService.deleteAppointmentV2Manager;
    this.getAppointmentsV2Manager = appointmentService.getAppointmentsV2Manager;
    this.postAppointmentV2Manager = appointmentService.postAppointmentV2Manager;
    this.putAppointmentV2Manager = appointmentService.putAppointmentV2Manager;
    this.deleteBookingInterval = appointmentService.deleteBookingInterval;
    this.postBookingInterval = appointmentService.postBookingInterval;
    this.putBookingInterval = appointmentService.putBookingInterval;
    this.postManagerBookingInterval = appointmentService.postManagerBookingInterval;
    this.deleteService = appointmentService.deleteService;
    this.getHcpServices = appointmentService.getHcpServices;
    this.getManagerServices = appointmentService.getManagerServices;
    this.getServiceTemplate = appointmentService.getServiceTemplate;
    this.postService = appointmentService.postService;
    this.putService = appointmentService.putService;

    const therapistService = createTherapistService({ transportV1: this.transport, transportV2: this.transportV2 });
    this.getTherapistActivePatients = therapistService.getTherapistActivePatients;
    this.getTherapistCommissions = therapistService.getTherapistCommissions;
    this.getTherapistEnrolledPatients = therapistService.getTherapistEnrolledPatients;
    this.getTherapistEnrollments = therapistService.getTherapistEnrollments;
    this.getTherapistInvitations = therapistService.getTherapistInvitations;
    this.getTherapistPatientsFunctionality = therapistService.getTherapistPatientsFunctionality;
    this.getTherapistPatientsPain = therapistService.getTherapistPatientsPain;
    this.getTherapistRating = therapistService.getTherapistRating;
    this.postTherapistInvitation = therapistService.postTherapistInvitation;
    this.putResendTherapistInvitation = therapistService.putResendTherapistInvitation;

    const clinicPartnerService = createClinicPartnerService({
      transportV2: this.transportV2,
    });
    this.getClinicPartnerActivePatients = clinicPartnerService.getClinicPartnerActivePatients;
    this.getClinicPartnerAvgFunctionality = clinicPartnerService.getClinicPartnerAvgFunctionality;
    this.getClinicPartnerAvgNps = clinicPartnerService.getClinicPartnerAvgNps;
    this.getClinicPartnerAvgPain = clinicPartnerService.getClinicPartnerAvgPain;
    this.getClinicPartnerEnrolledPatients = clinicPartnerService.getClinicPartnerEnrolledPatients;
    this.getClinicPartnerInvitations = clinicPartnerService.getClinicPartnerInvitations;
    this.getClinicPartnerPatients = clinicPartnerService.getClinicPartnerPatients;
    this.getClinicPartnersMembers = clinicPartnerService.getClinicPartnersMembers;
    this.getUsProviderGroupClinics = clinicPartnerService.getUsProviderGroupClinics;
    this.postClinicPartnerInvitation = clinicPartnerService.postClinicPartnerInvitation;
    this.postResendClinicPartnerInvitation = clinicPartnerService.postResendClinicPartnerInvitation;
  }

  getAuthenticatedUser(): Promise<GetAuthenticatedUserResponse> {
    return this.transportV2.get<GetAuthenticatedUserResponse>("/me").then(({ data }) => data);
  }

  getHealthReports(args: GetHealthReportsArgs): Promise<GetHealthReportsResponse> {
    const { patientId, treatment_id } = args;
    const params = { patient_id: patientId, includes: "answers", treatment_id };

    return this.transport.get(`/health_reports`, { params }).then(({ data }) => data);
  }

  getHealthReportsV2(args: GetHealthReportsV2Args): Promise<GetHealthReportsV2Response> {
    const { patientId, role = Roles.Therapist, treatment_id } = args;

    return this.transportV2
      .get<GetHealthReportsV2Response>(`${getRolePathPrefix(role)}/patients/${patientId}/health_reports`, {
        params: { ...(treatment_id && { treatment_id }) },
      })
      .then(({ data }) => data);
  }

  getMedicalReferrals(patientId: number): Promise<GetMedicalReferralsResponse> {
    return this.transport.get(`/patients/${patientId}/medical_referrals`).then(({ data }) => data);
  }

  getPainGraphData(args: GetPainGraphArgs): Promise<GetPainGraphResponse> {
    const { patientId, treatment_id } = args;
    const params = { treatment_id };

    return this.transport.get(`patients/${patientId}/widgets/reports`, { params }).then(({ data }) => data);
  }

  async getPatients(args: GetPatientsArgsSchema) {
    const { userID, config } = args;

    const response = await this.transport.get(`therapists/${userID}/patients`, config);
    return validateResponse(response, PatientsSchema.array(), false);
  }

  getPatient(args: GetPatientArgs): Promise<GetPatientResponse> {
    const { patientId, treatment_id } = args;
    const params = { treatment_id };

    return this.transport.get(`patients/${patientId}`, { params }).then(({ data }) => data);
  }

  getPatientCareEvents(args: GetPatientCareEventsArgs): Promise<GetPatientCareEventsResponse> {
    const { signed, patientId } = args;
    const params = { filter: signed ? "signed" : "unsigned" };

    return this.transport.get(`patients/${patientId}/care_events`, { params }).then(({ data }) => data);
  }

  getPatientCurrentCourse(args: GetCurrentCourseArgs): Promise<GetCurrentCourseResponse> {
    const { patientId, treatment_id } = args;
    const params = { treatment_id };

    return this.transport.get(`/patients/${patientId}/current_course`, { params }).then(({ data }) => data);
  }

  getPatientCurrentSubscription(
    args: GetPatientCurrentSubscriptionArgs
  ): Promise<GetPatientCurrentSubscriptionResponse> {
    const { patientId } = args;

    return this.transport.get(`patients/${patientId}/subscriptions/current`).then(({ data }) => data);
  }

  getPatientGoals(args: GetPatientGoalsArgs): Promise<GetPatientGoalsResponse> {
    const { patientId, currentPeriod = false, treatment_id } = args;
    const params = { period: currentPeriod ? "current" : undefined, ...(treatment_id && { treatment_id }) };

    return this.transport.get(`patients/${patientId}/goals`, { params }).then(({ data }) => data);
  }

  async getPatientHealthJournal(args: HealthJournalArgs): Promise<HealthJournal> {
    const { patientId, treatment_id } = args;
    const params = { treatment_id };

    const response = await this.transport.get(`patients/${patientId}/health_journal`, { params });
    return validateResponse(response, HealthJournalSchema, false);
  }

  getPatientIcdCodes(args: GetPatientIcdCodesArgs): Promise<GetPatientIcdCodesResponse> {
    const { patientId } = args;

    return this.transport.get(`patients/${patientId}/icd_codes`).then(({ data }) => data);
  }

  getPatientNote(args: GetPatientNoteArgs): Promise<GetPatientNoteResponse> {
    const { patientId, therapistId } = args;

    return this.transport.get(`/patients/${patientId}/note?therapist_id=${therapistId}`).then(({ data }) => data);
  }

  getPatientUnreadCommentsCount(patientId: number): Promise<GetPatientUnreadCommentsCountResponse> {
    return this.transport.get(`/patients/${patientId}/unread_comments_count`).then(({ data }) => data);
  }

  getPatientUsProviderGroupProfile(
    args: GetPatientUsProviderGroupProfileArgs
  ): Promise<GetPatientUsProviderGroupProfileResponse> {
    const { patientId } = args;

    return this.transport.get(`/patients/${patientId}/us_provider_group_profile`).then(({ data }) => data);
  }

  getPatientWidgetsPainLocation(
    args: GetPatientWidgetsPainLocationArgs
  ): Promise<GetPatientWidgetsPainLocationResponse> {
    const { patientId, treatment_id } = args;
    const params = { treatment_id };

    return this.transport.get(`patients/${patientId}/widgets/pain_locations`, { params }).then(({ data }) => data);
  }

  getPhysicalFunctionGraphData(args: GetPhysicalFunctionGraphArgs): Promise<GetPhysicalFunctionGraphResponse> {
    const { patientId, treatment_id } = args;
    const params = { treatment_id };

    return this.transport
      .get(`patients/${patientId}/widgets/functionality_history`, { params })
      .then(({ data }) => data);
  }

  async getPatientFeelingReports(args: FeelingReportArgs) {
    const { patientID, treatment_id } = args;
    const params = { treatment_id };
    const response = await this.transport.get(`/patients/${patientID}/widgets/feeling_reports`, { params });
    return validateResponse(response, FeelingReportSchema.array(), false);
  }

  getTherapistEarnings(args: GetTherapistEarningsArgs): Promise<GetTherapistEarningsResponse> {
    const { date, therapistId } = args;

    return this.transport
      .get(`dashboard/earnings_therapist?month=${date}&therapist_id=${therapistId}`)
      .then(({ data }) => data);
  }

  getPlansOfCare(args: GetPlansOfCareArgs): Promise<GetPlansOfCareResponse> {
    const { acknowledgedByPT, patientId } = args;

    return this.transport
      .get(`patients/${patientId}/us_patient_plans_of_care?acknowledged_by_therapist=${acknowledgedByPT}`)
      .then(({ data }) => data);
  }

  getPTCareEvents(args: GetPTCareEventsArgs): Promise<GetPTCareEventsResponse> {
    const { userId } = args;

    return this.transport.get(`therapists/${userId}/care_events?filter=unsigned`).then(({ data }) => data);
  }

  getUsPatientInsurance(args: GetUsPatientInsuranceArgs): Promise<GetUsPatientInsuranceResponse> {
    const { patientId } = args;

    return this.transport.get(`us_insurance/patient_insurance/${patientId}`).then(({ data }) => data);
  }

  async postUpdateInsurance(args: UpdateInsuranceArgs) {
    const { data: body } = args;
    const response = await this.transport.post("/us_insurance/update_insurances", body);
    return validateResponse(response, UpdateInsuranceResponseSchema, false);
  }

  putPatientCareEvent(args: PutPatientCareEventArgs): Promise<PutPatientCareEventResponse> {
    return this.transport.put(`care_events/${args.careEventId}`, args.data);
  }

  putPatientMarkAsPrio(args: PutPatientMarkAsPrioArgs): Promise<PutPatientMarkAsPrioResponse> {
    const { data: body, patientId } = args;

    return this.transport
      .put<PutPatientMarkAsPrioResponse>(`/patients/${patientId}/mark_as_prio`, body)
      .then(({ data }) => data);
  }

  putPlanOfCare(args: PutPlanOfCareArgs): Promise<PutPlanOfCareResponse> {
    return this.transport.put(`/us_patient_plans_of_care/${args.careEventId}`, args.data);
  }

  putTherapist(args: PutTherapistArgs): Promise<PutTherapistResponse> {
    const { data: body, therapistId } = args;

    return this.transport.put<PutTherapistResponse>(`/therapists/${therapistId}`, body).then(({ data }) => data);
  }

  async getPatientAvailableAilments(args: AvailableAilmentsArgs) {
    const { therapistID, patientID } = args;
    const response = await this.transport.get(`/therapists/${therapistID}/patients/${patientID}/available_ailments`);
    return validateResponse(response, AvailableAilmentsSchema, false);
  }

  async putPatientSwitchLateralLocation(args: SwitchLateralLocationArgs) {
    const { patientID, data: body } = args;
    const response = await this.transport.put(`/patients/${patientID}/lateral_location`, body);
    return validateResponse(response, SwitchLateralLocationSchema, false);
  }

  async postPatientSwitchTreatmentProgram(args: SwitchTreatmentProgramArgs) {
    const { data: body, patientID } = args;
    return this.transportV2.post(`/therapists/patients/${patientID}/treatment/switch`, body).then(({ data }) => data);
  }

  async getHCProfessionals(managerID: number) {
    const response = await this.transportV2.get(`/managers/${managerID}/professionals`);
    return validateResponse(response, HCProfessionalResponseSchema.array(), false);
  }

  async getManagerPatients(managerId: number): Promise<GetManagerPatientsResponse[]> {
    const response = await this.transportV2.get(`managers/${managerId}/patients`);
    return validateResponse(response, GetManagerPatientsResponseSchema.array(), false);
  }

  postDismissAppointmentSuggestion(
    args: PostDismissAppointmentSuggestionArgs
  ): Promise<PostDismissAppointmentSuggestionResponse> {
    const { data: body, patientId } = args;

    return this.transport.post(`/patients/${patientId}/dismiss_appointment_suggestion`, body).then(({ data }) => data);
  }

  postPatientEndTreatment(patientId: number) {
    return this.transport.post(`/patients/${patientId}/end_treatment`).then(({ data }) => data);
  }

  postMedicalNoteAddendum(args: PostMedicalNoteAddendumArgs): Promise<CareEvent> {
    const { body, careEventId } = args;

    return this.transport.post(`/care_events/${careEventId}/addendum`, { body }).then(({ data }) => data);
  }

  postCableConnectionUrls(): Promise<{ url: string }> {
    return this.transport.post(`/cable/connection_urls`).then(({ data }) => data);
  }

  async postStartTreatment(patientID: number) {
    return this.transportV2.post(`/patients/${patientID}/treatment/start`).then(({ data }) => data);
  }

  // v2
  async getUserCourse(patientId: number) {
    const response = await this.transportV2.get(`/patients/${patientId}/current_course`);
    return validateResponse(response, GetUserCourseResponseSchema, false);
  }

  async getUserCourseTemplate(templateId: number) {
    const response = await this.transportV2.get(`/treatment_templates/${templateId}`);
    return validateResponse(response, GetUserCourseTemplateResponseSchema, false);
  }

  async putUserCourse(patientId: number, course: PutUserCoursePayload) {
    return this.transportV2.put(`/patients/${patientId}/current_course`, course).then(({ data }) => data);
  }

  async getUserCourseTemplates() {
    const response = await this.transportV2.get(`/treatment_templates`);
    return validateResponse(response, GetUserCourseTemplatesResponseSchema, false);
  }

  async createUserCourseTemplate(therapistId: number, course: CreateUserCourseTemplatePayload) {
    return this.transportV2.post(`/therapists/${therapistId}/treatment_templates`, course).then(({ data }) => data);
  }

  async updateUserCourseTemplate(therapistId: number, templateId: number, course: UpdateUserCourseTemplatePayload) {
    return this.transportV2
      .put(`/therapists/${therapistId}/treatment_templates/${templateId}`, course)
      .then(({ data }) => data);
  }

  async deleteUserCourseTemplate(therapistId: number, templateId: number) {
    return this.transportV2
      .delete(`/therapists/${therapistId}/treatment_templates/${templateId}`)
      .then(({ data }) => data);
  }

  // LeifGPT
  async getSmartCommentReplies(args: GetSmartRepliesArgs) {
    const { therapistId, patientId } = args;
    const response = await this.transportLeifGPT.get(`/exercise_comment_replies/${therapistId}/${patientId}`);
    return validateResponse(response, CommentRepliesSchema, false);
  }

  async getSmartMessageReplies(args: GetSmartRepliesArgs) {
    const { therapistId, patientId } = args;
    const response = await this.transportLeifGPT.get(`/message_replies/${therapistId}/${patientId}`);
    return validateResponse(response, MessageRepliesSchema, false);
  }

  async postSmartReplyFeedback(id: number, feedback: "positive" | "negative") {
    await this.transportLeifGPT.post(`/smart_replies/${id}/feedback`, { feedback });
  }

  // Treatment Activity Log
  async getTreatmentActivityLogs({ pagination, sort }: QueryOptionsArgs) {
    const response = await this.transportV2.get("/therapist/treatment_activity_logs/pending", {
      params: { ...pagination, ...sort },
    });
    return validateResponse(response, TreatmentActivityLogSchema, false);
  }

  async getTreatmentActivityLogCategories() {
    const response = await this.transportV2.get("/therapist/treatment_activity_log/categories");
    return validateResponse(response, TreatmentActivityLogCategoriesSchema, false);
  }

  async getTreatmentActivityLogsPatient(patientID: number) {
    const response = await this.transportV2.get(`/therapist/treatment_activity_logs/patients/${patientID}`);
    return validateResponse(response, TreatmentActivityLogsPatientSchema, false);
  }

  async postTreatmentActivityLog(args: PostTreatmentActivityLogArgs) {
    const { data: body } = args;
    return this.transportV2.post("/therapist/treatment_activity_log", body).then(({ data }) => data);
  }

  async putTreatmentActivityLog(args: PutTreatmentActivityLogArgs) {
    const { id, data: body } = args;
    const response = await this.transportV2.put(`/therapist/treatment_activity_log/${id}`, body);
    return validateResponse(response, PostTreatmentActivityLogResponseSchema, false);
  }

  async getCareEventsSuggested(patientIDs: number[]) {
    const response = await this.transportV2.get("/therapist/care_events/suggested", {
      params: { patient_id: patientIDs },
      paramsSerializer: params => {
        return queryString.stringify(params);
      },
    });
    return validateResponse(response, CareEventSuggestedSchema.array(), false);
  }

  async postCareEventSuggested(args: PostCareEventSuggestedArgs) {
    const { data: body, patientID } = args;
    return this.transport.post(`/patients/${patientID}/care_events/suggested`, body).then(({ data }) => data);
  }

  async getCareEventsBilling() {
    const response = await this.transportV2.get("/managers/billing/care_events");
    return validateResponse(response, CareEventBillingSchema.array(), false);
  }

  async putCareEventBilling(args: PutCareEventBillingArgs) {
    const { data: body } = args;
    return this.transportV2.put("/managers/billing/care_events/update", body).then(({ data }) => data);
  }

  async getCareEvent(careEventID: number) {
    return this.transport.get(`/care_events/${careEventID}`).then(({ data }) => data);
  }

  async getEarningsUsTherapist(args: EarningsUsTherapistArgs) {
    const { therapist_id, start_date } = args;

    return this.transport
      .get("/dashboard/earnings_us_therapist", {
        params: {
          therapist_id,
          start_date,
        },
      })
      .then(({ data }) => data);
  }

  async getEnrollmentBonuses(args: EarningsUsTherapistArgs) {
    const { therapist_id, start_date } = args;

    return this.transport
      .get("/dashboard/enrollment_bonuses", {
        params: {
          therapist_id,
          start_date,
        },
      })
      .then(({ data }) => data);
  }

  // PHYSICIAN ENDPOINTS - START
  async getPhysicianHandledPatients({ limit, page }: PaginationArgs) {
    const response = await this.transportV2.get(`/physicians/patients/handled?limit=${limit}&page=${page}`);
    return validateResponse(response, HandledPatientsResponseSchema, false);
  }

  async getPhysicianUnhandledPatients({ limit, page }: PaginationArgs) {
    const response = await this.transportV2.get(`/physicians/patients/unhandled?limit=${limit}&page=${page}`);
    return validateResponse(response, UnhandledPatientsResponseSchema, false);
  }

  async getPhysicianHandledPatient(patientID: number) {
    const response = await this.transportV2.get(`/physicians/patients/${patientID}/handled`);
    return validateResponse(response, HandledPatientSchema, false);
  }

  async getPhysicianUnhandledPatient(patientID: number) {
    const response = await this.transportV2.get(`/physicians/patients/${patientID}/unhandled`);
    return validateResponse(response, UnhandledPatientResponseSchema, false);
  }

  async getPhysicianMedicalReferral(medicalReferralID: number) {
    const response = await this.transportV2.get(`/physicians/medical_referrals/${medicalReferralID}`);
    return validateResponse(response, PhysicianMedicalReferralSchema, false);
  }

  async postPhysicianMedicalReferral(args: PostArgs) {
    const { data: body } = args;
    const response = await this.transportV2.post("/physicians/medical_referrals", body);
    return validateResponse(response, PhysicianMedicalReferralSchema, false);
  }

  async putPhysicianMedicalReferral(args: PutPhysicianMedicalReferralArgs) {
    const { data: body, medicalReferralID } = args;
    const response = await this.transportV2.put(`/physicians/medical_referrals/${medicalReferralID}`, body);
    return validateResponse(response, PhysicianMedicalReferralSchema, false);
  }

  async getPhysicianPlanOfCare(planOfCareID: number) {
    const response = await this.transportV2.get(`/physicians/plans_of_care/${planOfCareID}`);
    return validateResponse(response, PhysicianPlanOfCareSchema, false);
  }

  async postPhysicianPlanOfCare(args: PostArgs) {
    const { data: body } = args;
    const response = await this.transportV2.post("/physicians/plans_of_care", body);
    return validateResponse(response, PhysicianPlanOfCareSchema, false);
  }

  async putPhysicianPlanOfCare(args: PutPhysicianPlanOfCareArgs) {
    const { data: body, planOfCareID } = args;
    const response = await this.transportV2.put(`/physicians/plans_of_care/${planOfCareID}`, body);
    return validateResponse(response, PhysicianPlanOfCareSchema, false);
  }
  // PHYSICIAN ENDPOINTS - END

  async getManagerMedicalReferral(medicalReferralID: number) {
    const response = await this.transportV2.get(`/managers/medical_referrals/${medicalReferralID}`);
    return validateResponse(response, PhysicianMedicalReferralSchema, false);
  }

  async getManagerPlanOfCare(planOfCareID: number) {
    const response = await this.transportV2.get(`/managers/plans_of_care/${planOfCareID}`);
    return validateResponse(response, PhysicianPlanOfCareSchema, false);
  }

  async getUsStates() {
    const response = await this.transportV2.get("/us_states");
    return validateResponse(response, UsStatesResponseSchema, false).us_states;
  }

  // PUBLIC ENDPOINTS - START
  async getPotentialPayers(args: GetPotentialPayersArgs) {
    const { patient_id, zip_code } = args;
    const response = await this.transport.get("/public/us_insurance/potential_payers", {
      params: { filter: "recommended", patient_id, zip_code },
    });
    return validateResponse(response, PotentialPayerSchema.array(), false);
  }
  // PUBLIC ENDPOINTS - END

  async addressAutoComplete(args: AddressAutoCompleteArgs) {
    const { countryCode, query } = args;
    const response = await this.transportV2.get("/geo/autocomplete", {
      params: {
        q: query,
        limit: 5,
        result_types: "address",
        ...(countryCode ? { in: `countryCode:${countryCode}` } : {}),
      },
    });
    return validateResponse(response, AddressResponseSchema, false);
  }
}
